import { buildLegacyParityEvent } from '@galapagos/product-analytics';
import { trackLegacyParityEventWhenReady } from '@/app/analytics';
import { SIGNUP_OFF_PATH_PAGES, type SignupPageId } from '../off-path-pages';
import { SIGNUP_STEPS, SSO_ONLY_STEPS } from '../steps';
import { isDevGapFilledSession } from './signup-api/dev-gap-fill';

/**
 * Production's `Growth.RegFlow.*` events, so Growth's funnels keep reading the
 * same names after the signup surface moves. Properties carry no personal data
 * and no campaign params: the PostHog SDK adds those from the landing URL.
 */

// Production's labels for its first screens; any other page reports its own id.
const ENTRY_SCREEN_LABELS: Partial<Record<SignupPageId, string>> = {
  context: 'context',
  substance: 'substance_selection',
  'legal-name': 'lighterEligibilityName',
  error: 'internalError',
};

// Pages production never counts as a flow entry: a direct download link has
// its own event there, Book a call is reached from the footer or an error, and
// a returning member's login sends a rejoin event instead.
const NOT_FLOW_ENTRIES: ReadonlySet<SignupPageId> = new Set([
  'download',
  'book-a-call',
  'login',
  'login-code',
  'login-no-progress',
]);

const SIGNUP_PAGES: readonly SignupPageId[] = [
  ...SIGNUP_STEPS,
  ...SSO_ONLY_STEPS,
  ...SIGNUP_OFF_PATH_PAGES,
];

const VIEWED_MARKER_KEY = 'galapagos.signup.regflow-viewed';

// Stands in for the tab marker when session storage is unavailable, so the
// event still fires once per page load rather than on every step.
let viewedThisPageLoad = false;

export function regFlowEntryScreen(page: SignupPageId): string {
  return ENTRY_SCREEN_LABELS[page] ?? page;
}

/** The signup page a path renders, or null for the entry route, legal pages and anything else. */
export function signupPageFromPath(pathname: string): SignupPageId | null {
  const match = /^\/([^/?#]+)\/?$/.exec(pathname);
  if (!match) return null;
  return SIGNUP_PAGES.find((page) => page === match[1]) ?? null;
}

/** Claims the once-per-tab-session Viewed slot; false when it was already taken. */
function claimViewed(): boolean {
  try {
    if (window.sessionStorage.getItem(VIEWED_MARKER_KEY) !== null) return false;
    window.sessionStorage.setItem(VIEWED_MARKER_KEY, '1');
    return true;
  } catch {
    if (viewedThisPageLoad) return false;
    viewedThisPageLoad = true;
    return true;
  }
}

/** Fires `Growth.RegFlow.Viewed` on the first flow page a tab session renders. */
export function trackRegFlowViewed(pathname: string): void {
  const page = signupPageFromPath(pathname);
  if (page === null || NOT_FLOW_ENTRIES.has(page) || !claimViewed()) return;
  const entry_screen = regFlowEntryScreen(page);
  void trackLegacyParityEventWhenReady(() =>
    buildLegacyParityEvent('Growth.RegFlow.Viewed', { entry_screen }),
  );
}

/**
 * Fires `Growth.RegFlow.MemberRegistered` for the registration-profile save
 * that completed registration. The caller gates on the server's
 * `firstCompletion`, so a re-save never counts a second registration.
 * Conversion events stay quiet in a dev run that used fake data, so clicks
 * through it never count as signups in PostHog or anything fed from it.
 */
export function trackRegFlowMemberRegistered(): void {
  if (isDevGapFilledSession()) return;
  void trackLegacyParityEventWhenReady(() =>
    buildLegacyParityEvent('Growth.RegFlow.MemberRegistered', {}),
  );
}

/**
 * Fires `Growth.RegFlow.EmployerSearched` for a directory pick or a settled
 * empty search. Never the typed text, the picked name or its ref: the
 * directory is decoy-salted, and what was searched stays out of analytics.
 */
export function trackRegFlowEmployerSearched(searchResult: 'covered' | 'no_results'): void {
  void trackLegacyParityEventWhenReady(() =>
    buildLegacyParityEvent('Growth.RegFlow.EmployerSearched', {
      search_type: 'employer',
      search_result: searchResult,
    }),
  );
}

/** Fires `Growth.RegFlow.QuizCompleted` once the quiz answers are saved with the intake finalize. */
export function trackRegFlowQuizCompleted(): void {
  if (isDevGapFilledSession()) return;
  void trackLegacyParityEventWhenReady(() =>
    buildLegacyParityEvent('Growth.RegFlow.QuizCompleted', {}),
  );
}

export function resetRegFlowAnalyticsForTests(): void {
  viewedThisPageLoad = false;
}
